import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import ScrollToTopButton from "../../resources/components/ScrollToTopButton";
import { WorkServiceImpl } from "../../../services/WorkServiceImpl";
import { DEFAULT_PAGE, RECORDS_PER_PAGE } from "../../../domain/constants";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import {
    workSearchSortFields,
    workSearchSortSchemaFactory,
} from "../../../validation-schemas/work-schemas/WorkSearchSortSchema";
import CatalogSort from "../../resources/components/work-components/sort/CatalogSort";
import WorkList from "../../resources/components/work-components/work-list/WorkList";
import { useSelector } from "react-redux";
import { selectWorks } from "../../../slices/works-slice";
import { useUrlWorksSearchParams, useWorksSearchParams } from "../../../custom-hooks/domain/useWorksSearchParams";
import { WorkListType } from "../../../domain/models/Work";
import { getUserCoords, selectUser } from "../../../slices/user-slice";

const CreatedOrdersPage: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [page, setPage] = useState(0);

    const works = useSelector(selectWorks);
    const user = useSelector(selectUser());

    const params = useUrlWorksSearchParams();
    const { form, setErrorKey } = useCustomFormik({
        initial: params,
        fields: workSearchSortFields,
        schema: workSearchSortSchemaFactory(t),
        onSubmit: async () => {
            uploadWorks(true);
        },
    });
    const { resetUrlParams } = useWorksSearchParams(form);

    const uploadWorks = async (resetPage: boolean, resetForm = false) => {
        const currentPage = resetPage ? DEFAULT_PAGE : page + 1;
        const result = await dispatch(
            WorkServiceImpl.getWorksThunk({
                page: currentPage,
                limit: RECORDS_PER_PAGE,
                form: resetForm ? null : form.values,
                getCurrentUserWorks: true,
                coords: await getUserCoords(user),
            })
        );
        if (result.meta.requestStatus !== "fulfilled") {
            setErrorKey("errors.common");
        }
        setPage(currentPage);
    };

    const uploadWorksCallback = useCallback(async () => {
        uploadWorks(false);
    }, [form.values, page]);

    const resetFormCallback = async () => {
        form.resetForm();
        resetUrlParams();
        uploadWorks(true, true);
    };

    return (
        <div className="relative mx-auto dmin2:w-[1280px] di6:w-[800px] ti6:w-[650px] ti7:w-[560px] tmax4:w-[360px] mb-[80px]">
            <h1 className="H32B dmax2:H24B text-textWhite mb-[50px] dmax2:mb-6">
                {t("applications.created", { value: works.meta.createdWorksCount })}
            </h1>
            <CatalogSort form={form} resetFormCallback={resetFormCallback} />
            <div className="mt-10">
                <WorkList
                    type={WorkListType.COMMON}
                    useCardVersionWithApplication
                    uploadWorksCallback={uploadWorksCallback}
                />
            </div>
            <ScrollToTopButton />
        </div>
    );
};

export default CreatedOrdersPage;
